import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Download, ExternalLink, Gem, ShieldCheck, Clock, Lock } from "lucide-react";

const GOLD = "#C20511";
const BG = "#0B1420";

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch (e) { return null; }
}

const Shell = ({ children, testid }) => (
  <div className="min-h-screen grid place-items-center px-6 text-center" style={{ background: BG }} data-testid={testid}>{children}</div>
);

export default function PackSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  const [k, setK] = useState(null);
  const [pw, setPw] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback((key) => {
    const url = key ? `/public/pack/${token}?k=${encodeURIComponent(key)}` : `/public/pack/${token}`;
    api.get(url).then((r) => setData(r.data))
      .catch((e) => setData(e?.response?.status === 410 ? "expired" : null));
  }, [token]);

  useEffect(() => { load(null); }, [load]);

  const doUnlock = async (e) => {
    e && e.preventDefault();
    if (!pw.trim()) return;
    setUnlocking(true); setErr("");
    try {
      const { data: res } = await api.post(`/public/pack/${token}/unlock`, { password: pw });
      setK(res.k);
      load(res.k);
    } catch (ex) {
      const s = ex?.response?.status;
      setErr(s === 429 ? "Too many attempts - please wait a few minutes." : "Incorrect passcode. Please try again.");
    } finally { setUnlocking(false); }
  };

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/pack/${token}/pdf${k ? `?k=${encodeURIComponent(k)}` : ""}`;

  if (data === undefined)
    return <Shell><div className="text-slate-300 inline-flex items-center"><Loader2 className="animate-spin mr-2" /> Loading proposal…</div></Shell>;
  if (data === "expired")
    return <Shell testid="pack-share-expired"><div className="text-slate-300"><Clock className="mx-auto mb-3 text-slate-500" /><p>This proposal link has expired.</p><p className="text-slate-500 text-sm mt-1">Please request an updated link from your consultant.</p></div></Shell>;
  if (data === null)
    return <Shell testid="pack-share-invalid"><span className="text-slate-300">This link is invalid or has been revoked.</span></Shell>;

  // ---- Passcode gate ----
  if (data.locked) {
    return (
      <Shell testid="pack-share-locked">
        <form onSubmit={doUnlock} className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-left">
          <div className="flex items-center gap-2 mb-1">
            {data.logo ? <img src={data.logo} alt="logo" className="h-7 max-w-[130px] object-contain" /> : <Gem style={{ color: GOLD }} size={20} />}
            <span className="text-slate-200 font-extrabold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>{data.company || "SYMIN"}</span>
          </div>
          <div className="mx-auto my-4 grid h-11 w-11 place-items-center rounded-full" style={{ background: `${GOLD}1A` }}>
            <Lock size={18} style={{ color: GOLD }} />
          </div>
          <h1 className="text-xl font-extrabold text-slate-100 text-center" style={{ fontFamily: "Manrope, sans-serif" }}>Passcode required</h1>
          <p className="text-slate-400 text-sm text-center mt-1 mb-4">This proposal is protected. Enter the passcode your consultant shared.</p>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Enter passcode" autoFocus
            data-testid="pack-unlock-input"
            className="w-full rounded-lg border border-white/15 bg-[#0F1B2B] px-3 py-2.5 text-slate-100 placeholder:text-slate-500 outline-none focus:border-[color:var(--g)]" style={{ "--g": GOLD }} />
          {err && <p className="text-red-400 text-xs mt-2" data-testid="pack-unlock-error">{err}</p>}
          <button type="submit" disabled={unlocking} data-testid="pack-unlock-submit"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.01] disabled:opacity-60" style={{ background: GOLD }}>
            {unlocking ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} Unlock proposal
          </button>
        </form>
      </Shell>
    );
  }

  // ---- Full view ----
  const expOn = fmtDate(data.expires_at);
  return (
    <div className="min-h-screen text-slate-100" style={{ background: BG, fontFamily: "IBM Plex Sans, sans-serif" }} data-testid="pack-share-page">
      <div className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          {data.logo ? <img src={data.logo} alt="logo" className="h-9 max-w-[150px] object-contain" />
            : <Gem style={{ color: GOLD }} size={24} />}
          <div className="min-w-0">
            <div className="font-extrabold tracking-tight truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{data.company || "SYMIN"}</div>
            <div className="text-[11px] text-slate-400">{data.client_name ? `Prepared for ${data.client_name}` : "Exploration Proposal"}</div>
          </div>
          <div className="ml-auto text-[11px] uppercase tracking-widest font-semibold hidden sm:flex items-center gap-1.5" style={{ color: GOLD }}>
            <ShieldCheck size={13} /> Read-only
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div>
          <p className="text-[12px] font-semibold tracking-[0.2em] uppercase" style={{ color: GOLD }}>Exploration Proposal Pack</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="pack-share-title">{data.title}</h1>
          {data.blurb && <p className="text-slate-400 mt-3 text-sm max-w-2xl leading-relaxed" data-testid="pack-share-blurb">{data.blurb}</p>}
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="pack-share-commodities">
          {(data.commodities || []).map((c) => (
            <span key={c.name} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-200">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />{c.label}
            </span>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="pack-share-open"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-transform hover:scale-[1.02]" style={{ background: GOLD }}>
            <ExternalLink size={15} /> Open in new tab
          </a>
          <a href={pdfUrl} download data-testid="pack-share-download"
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-white/5 transition-colors">
            <Download size={15} /> Download PDF
          </a>
          {expOn && <span className="text-[11px] text-slate-500 inline-flex items-center gap-1.5"><Clock size={12} /> Link expires {expOn}</span>}
        </div>

        <div className="rounded-2xl overflow-hidden border border-white/10 bg-white shadow-2xl" data-testid="pack-share-viewer">
          <object data={pdfUrl} type="application/pdf" className="w-full" style={{ height: "82vh" }} aria-label="Proposal pack PDF">
            <div className="p-10 text-center text-slate-600">
              Your browser can’t display the PDF inline.
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="ml-1 underline" style={{ color: GOLD }}>Open the proposal here.</a>
            </div>
          </object>
        </div>

        <p className="text-[11px] text-slate-500 text-center pt-2">
          Outputs identify spectral anomalies and exploration targets only - not confirmed deposits. Ground verification is required.
        </p>
      </div>
    </div>
  );
}
